/* import __COLOCATED_TEMPLATE__ from './channel-limit.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'embercom/services/intl';
import {
  EMAIL_CHANNEL_LIMIT_FILTER_TYPE,
  MESSENGER_CHANNEL_LIMIT_FILTER_TYPE,
  ASSIGNMENT_LIMIT_COMPARATORS,
} from 'embercom/lib/settings/filters/constants';

type ChannelType = 'email' | 'messenger';

interface Filter {
  type: string;
  comparator: string;
  value: number | undefined;
}

interface Signature {
  Args: {
    channelType: ChannelType;
    filter: Filter;
    updateFilter: (filter: Filter) => void;
  };
}

const CHANNEL_TYPE_TO_FILTER_TYPE: Record<ChannelType, string> = {
  email: EMAIL_CHANNEL_LIMIT_FILTER_TYPE,
  messenger: MESSENGER_CHANNEL_LIMIT_FILTER_TYPE,
};

export default class ChannelLimit extends Component<Signature> {
  @service declare intl: IntlService;
  @tracked channelLimitValue: number | undefined = this.args.filter.value;
  @tracked selectedComparator: string = this.args.filter.comparator;
  @tracked popoverOpen = false;

  get filterType(): string {
    return CHANNEL_TYPE_TO_FILTER_TYPE[this.args.channelType];
  }

  @action popoverClosed(): void {
    this.popoverOpen = false;
  }

  private comparatorLabelKey(comparator: string): string {
    switch (comparator) {
      case ASSIGNMENT_LIMIT_COMPARATORS.greaterThan:
        return 'settings.teammate-workload.filters.label-comparators.gt';
      case ASSIGNMENT_LIMIT_COMPARATORS.lessThan:
        return 'settings.teammate-workload.filters.label-comparators.lt';
      case ASSIGNMENT_LIMIT_COMPARATORS.is:
        return 'settings.teammate-workload.filters.label-comparators.eq';
      case ASSIGNMENT_LIMIT_COMPARATORS.isNot:
        return 'settings.teammate-workload.filters.label-comparators.ne';
      default:
        return 'settings.teammate-workload.filters.label-comparators.any';
    }
  }

  private dropdownComparatorKey(comparator: string): string {
    switch (comparator) {
      case ASSIGNMENT_LIMIT_COMPARATORS.greaterThan:
        return 'settings.teammate-workload.filters.dropdown-comparators.gt';
      case ASSIGNMENT_LIMIT_COMPARATORS.lessThan:
        return 'settings.teammate-workload.filters.dropdown-comparators.lt';
      case ASSIGNMENT_LIMIT_COMPARATORS.is:
        return 'settings.teammate-workload.filters.dropdown-comparators.eq';
      case ASSIGNMENT_LIMIT_COMPARATORS.isNot:
        return 'settings.teammate-workload.filters.dropdown-comparators.ne';
      default:
        return 'settings.teammate-workload.filters.dropdown-comparators.any';
    }
  }

  get comparisonOptions(): { comparator: string; dropdownLabel: string; disableInput: boolean }[] {
    return Object.values(ASSIGNMENT_LIMIT_COMPARATORS).map((comparator: string) => ({
      comparator,
      dropdownLabel: this.intl.t(this.dropdownComparatorKey(comparator)),
      disableInput: comparator === ASSIGNMENT_LIMIT_COMPARATORS.hasAnyValue,
    }));
  }

  get filterDetails(): string {
    return this.intl.t('settings.teammate-workload.filters.assignment-limit-details', {
      comparator: this.intl.t(this.comparatorLabelKey(this.args.filter.comparator)),
      assignmentLimit:
        this.args.filter.comparator !== ASSIGNMENT_LIMIT_COMPARATORS.hasAnyValue
          ? this.args.filter.value
          : '',
    });
  }

  get filterLabel(): string {
    if (this.args.channelType === 'email') {
      return this.intl.t('settings.teammate-workload.filters.email-channel-limit-label');
    }
    return this.intl.t('settings.teammate-workload.filters.messenger-channel-limit-label');
  }

  get filterIcon(): string {
    return this.args.channelType === 'email' ? 'email' : 'messenger';
  }

  @action revertState(): void {
    this.channelLimitValue = this.args.filter.value;
    this.selectedComparator = this.args.filter.comparator;
  }

  @action selectComparator(comparator: string): void {
    this.selectedComparator = comparator;
  }

  @action updateFilter(): void {
    this.args.updateFilter({
      type: this.filterType,
      comparator: this.selectedComparator,
      value:
        this.selectedComparator === ASSIGNMENT_LIMIT_COMPARATORS.hasAnyValue ||
        this.channelLimitValue === undefined
          ? undefined
          : parseInt(String(this.channelLimitValue), 10),
    });
  }
}
